import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import stationManagementApi from '../../../../utils/api/station-management';

interface ChartDataProperties {
  label: string;
  paidTransactionCount: number;
  freeTransactionCount: number;
  [key: string]: string | number; // Add this to allow dynamic properties
}

/**
 * Guest Charging Chart
 * @returns
 */
function GuestChargingChart() {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [chartLabelData, setChartLabelData] = useState([
    {
      label: t('paid'),
      key: 'paid',
      value: 30,
      color: '#F04438',
    },
    {
      label: t('guest'),
      key: 'guest',
      value: 50,
      color: '#CF5151',
    },
  ]);

  /**
   * Function to show custom tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;

      return (
        <div className="guest-charging-tooltip">
          <p className="label">{data?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#2AA700' }}>
              {t('paidCharging')}: {data?.paidTransactionCount || 0} {t('hrs')}
            </p>
            <p style={{ color: '#CF5151' }}>
              {t('guestChargingLabel')}: {data?.freeTransactionCount || 0} {t('hrs')}
            </p>
          </div>
        </div>
      );
    }

    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param {'last24hours' | 'last7days' | 'lastMonth' | 'last12months'} range The time range to fetch data for.
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    setIsLoading(true);
    try {
      if (!selectedChargingPoint?.id) return;
      const response = await stationManagementApi.fetchGuestChargingchart({
        timePeriod: range,
        chargingStation: selectedChargingPoint.id,
      });

      if (response.status === 200) {
        let transformedData: ChartDataProperties[];

        switch (range) {
          case 'last12months':
            transformedData = fillMissingMonthsGeneric(response.data).map((item) => ({
              label: item.label,
              paidTransactionCount: (item as any).paidTransactionCount || 0,
              freeTransactionCount: (item as any).freeTransactionCount || 0,
            }));
            break;
          case 'lastMonth':
            transformedData = fillMissingLastNDaysGeneric(response.data, 30).map((item) => ({
              label: item.label,
              paidTransactionCount: (item as any).paidTransactionCount || 0,
              freeTransactionCount: (item as any).freeTransactionCount || 0,
            }));
            break;
          case 'last7days':
            transformedData = fillMissingLastNDaysGeneric(response.data, 7).map((item) => ({
              label: item.label,
              paidTransactionCount: (item as any).paidTransactionCount || 0,
              freeTransactionCount: (item as any).freeTransactionCount || 0,
            }));
            break;
          case 'last24hours':
            transformedData = fillMissingLast24HrsGeneric(response.data).map((item) => ({
              label: item.label,
              paidTransactionCount: (item as any).paidTransactionCount || 0,
              freeTransactionCount: (item as any).freeTransactionCount || 0,
            }));
            break;
          default:
            transformedData = [];
        }

        setBarGraphData(transformedData);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, [selectedChargingPoint?.id]);

  useEffect(() => {
    setChartLabelData([
      {
        label: t('paid'),
        key: 'paid',
        value: barGraphData.reduce(
          (accumulator, each) => accumulator + Number(each.paidTransactionCount),
          0,
        ),
        color: '#2AA700',
      },
      {
        label: t('guest'),
        key: 'guest',
        value: barGraphData.reduce(
          (accumulator, each) => accumulator + Number(each.freeTransactionCount),
          0,
        ),
        color: '#CF5151',
      },
    ]);
  }, [barGraphData]);

  return (
    <div className="guest-charging-chart">
      <div className="guest-charging-container">
        {isLoading ? (
          <div className="loader">
            <BatteryLoader />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barGraphData} barCategoryGap={25}>
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                fontSize={12}
                stroke="#535353"
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Bar dataKey="paidTransactionCount" stackId="a" barSize={26} fill="#2AA700" />
              <Bar dataKey="freeTransactionCount" stackId="a" barSize={26} fill="#F04438" />
            </BarChart>
          </ResponsiveContainer>
        )}

        <div className="report-header">
          <CustomToggleButton
            labels={chartTimePeriod.map((duration) => t(duration.label))}
            onToggle={(index, label) =>
              fetchPowerConsumedReport(
                chartTimePeriod.find((duration) => t(duration.label) === label)?.value ||
                  'last12months',
              )
            }
            defaultActiveIndex={0}
            type="chart"
          />
        </div>
      </div>
      <div className="guest-charging-chart-count">
        {chartLabelData.map((eachLabel) => (
          <div
            className="each-label-container"
            style={{ '--bgColor': eachLabel.color } as React.CSSProperties}>
            <span>{eachLabel.label}</span>
            <span>{eachLabel.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GuestChargingChart;
